import React from "react";
import { StepDefinition, FieldDefinition } from "../../../../types/types";
import {
  renderSingleField,
  renderNestedGroup,
  buildOptions,
  humanizeLabel,
  isFieldVisible,
} from "../dealFormFields";
import {
  getOutcomePolicyFormValue,
  OutcomeRegistry,
  PayoutComponent,
} from "../../../../utils/opportunities/outcomeRegistryHelpers";
import { PayoutOutcomeEditor } from "../../../../components/opportunities/payoutOutcomeEditor";
import { DealFormData, FieldHelpers, FormValue, TemplateStep } from "../dealFormTypes";
import { TranslationFn } from "../dealFormFields";

export type PayoutPoliciesStepProps = {
  step: StepDefinition;
  stepId: string;
  formData: DealFormData;
  fieldHelpers: FieldHelpers;
  t: TranslationFn;
  updateFormAtPath: (path: string[], value: FormValue) => void;
  rawTemplateSteps: TemplateStep[];
};

export const PayoutPoliciesStep = ({
  step,
  stepId,
  formData,
  fieldHelpers,
  t,
  updateFormAtPath,
  rawTemplateSteps,
}: PayoutPoliciesStepProps): React.ReactElement | null => {
  const { updateFieldValue } = fieldHelpers;
  const fields = step.fields ?? [];
  const stepData = (formData[stepId] ?? {}) as Record<string, FormValue>;

  const renderField = (field?: FieldDefinition) => {
    if (!field) return null;
    if (!isFieldVisible(field, stepData, formData)) return null;
    const fieldKey = String(field.key ?? "");
    const isPayoutComponentField = fieldKey
      .toLowerCase()
      .replace(/[_\s-]+/g, "")
      .endsWith("components");
    const helpersForField = isPayoutComponentField
      ? {
          ...fieldHelpers,
          errors: {
            ...fieldHelpers.errors,
            [fieldKey]: undefined,
            [`${stepId}.${fieldKey}`]: undefined,
          },
        }
      : fieldHelpers;

    if (Array.isArray(field.fields) && field.fields.length > 0)
      return renderNestedGroup(stepId, field, stepData, helpersForField);
    return renderSingleField(
      stepId,
      field,
      stepData,
      undefined,
      undefined,
      helpersForField,
    );
  };

  const POLICY_SUFFIXES = ["payouttype", "policy", "type"];
  const COMPONENT_SUFFIXES = ["components"];

  const resolveRegistryOptions = (optionsFrom?: string) => {
    const path = String(optionsFrom || "").trim();
    const dotIndex = path.indexOf(".");
    if (dotIndex === -1) return [];

    const sourceStepId = path.slice(0, dotIndex);
    const sourceFieldKey = path.slice(dotIndex + 1);
    const sourceItems = formData?.[sourceStepId];
    if (!Array.isArray(sourceItems)) return [];

    const sourceStep = rawTemplateSteps.find(
      (item: TemplateStep) => item?.stepId === sourceStepId,
    );
    const sourceField = sourceStep?.fields?.find(
      (item: FieldDefinition) => item?.key === sourceFieldKey,
    );

    const seen = new Set<string>();
    const options: Array<{ key: string; label: string }> = [];

    sourceItems.forEach((item) => {
      const itemValues = item as Record<string, FormValue> | null | undefined;
      const rawValue = itemValues?.[sourceFieldKey];
      if (rawValue === undefined || rawValue === null || rawValue === "")
        return;

      const value = String(rawValue);
      if (seen.has(value)) return;
      seen.add(value);

      const sourceOptions = sourceField
        ? buildOptions(
            sourceField,
            itemValues as Record<string, FormValue>,
            formData,
            fieldHelpers.optionsBySource,
          )
        : [];
      const matched = sourceOptions.find((option) => option.key === value);

      options.push({
        key: value,
        label: matched?.label || humanizeLabel(value),
      });
    });

    return options;
  };

  const outcomeRegistry = (step as TemplateStep).outcomeRegistry as OutcomeRegistry;
  if (outcomeRegistry && fields.length === 0) {
    const coreData = formData?.core as Record<string, FormValue> | null | undefined;
    const stepValues = formData?.[stepId] as Record<string, FormValue> | null | undefined;
    const outcomesData = stepValues?.outcomes as Record<string, FormValue> | null | undefined;
    return (
      <PayoutOutcomeEditor
        outcomeRegistry={outcomeRegistry}
        baseAmount={Number(coreData?.baseAmount || 0)}
        currency={String(coreData?.currency || "")}
        allComponents={Array.isArray(formData?.components) ? formData.components as PayoutComponent[] : []}
        getPolicyValue={(row) =>
          getOutcomePolicyFormValue(row, outcomesData?.[row.key])
        }
        updatePolicyValue={(row, patch) => {
          const curr = getOutcomePolicyFormValue(
            row,
            outcomesData?.[row.key],
          );
          updateFieldValue(stepId, "outcomes." + row.key, { ...curr, ...patch });
        }}
        resolveRegistryOptions={(optionsFrom) =>
          optionsFrom
            ? resolveRegistryOptions(optionsFrom).map((o) => ({
                value: o.key,
                label: o.label,
              }))
            : []
        }
        tr={(key) => fieldHelpers.tr(key)}
      />
    );
  }

  const getPrefix = (key: string): string => {
    const lower = key.toLowerCase();
    const allSuffixes = [...POLICY_SUFFIXES, ...COMPONENT_SUFFIXES];
    for (const sfx of allSuffixes) {
      if (lower.endsWith(sfx))
        return lower.slice(0, lower.length - sfx.length);
    }
    return lower;
  };

  const isComponentField = (key: string) => {
    const lower = key.toLowerCase();
    return COMPONENT_SUFFIXES.some((sfx) => lower.endsWith(sfx));
  };

  const isPolicyField = (key: string) => {
    const lower = key.toLowerCase();
    return POLICY_SUFFIXES.some((sfx) => lower.endsWith(sfx));
  };

  const policyMap = new Map<string, FieldDefinition>();
  const componentMap = new Map<string, FieldDefinition>();
  const prefixOrder: string[] = [];
  for (const f of fields) {
    const key = f.key ?? "";
    const prefix = getPrefix(key);

    if (isPolicyField(key)) {
      if (!policyMap.has(prefix)) prefixOrder.push(prefix);
      policyMap.set(prefix, f);
    } else if (isComponentField(key)) {
      if (!componentMap.has(prefix) && !policyMap.has(prefix)) {
        prefixOrder.push(prefix);
      }
      componentMap.set(prefix, f);
    }
  }

  const seen = new Set<string>();
  const orderedPrefixes = prefixOrder.filter((p) => {
    if (seen.has(p)) return false;
    seen.add(p);
    return true;
  });

  return (
    <div className="space-y-4">
      {orderedPrefixes.map((prefix) => {
        const leftField = policyMap.get(prefix);
        const rightField = componentMap.get(prefix);

        const leftNode = renderField(leftField);
        const rightNode = renderField(rightField);

        if (!leftNode && !rightNode) return null;

        return (
          <div key={prefix} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {leftNode && (
              <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-3 min-h-[85px]">
                {leftNode}
              </div>
            )}
            {rightNode && (
              <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-3 min-h-[85px]">
                {rightNode}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};